import React, { useState } from 'react';
import { useLocation, useNavigate, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowLeft, CheckCircle, Sparkles, Leaf, Mail, BellRing } from 'lucide-react';
import SEO from '../components/SEO';
import NotifyMeDialog from '../components/NotifyMeDialog';
import ImageLightbox from '../components/ImageLightbox';
import { RANGE_PRODUCTS } from '../data/rangeProducts';

export default function RangeProduct() {
  const { pathname } = useLocation();
  const slug = pathname.replace(/^\//, '');
  const navigate = useNavigate();
  const [notify, setNotify] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const p = RANGE_PRODUCTS[slug];
  if (!p) return <Navigate to="/products" replace />;

  const productLd = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: p.name,
    category: p.category,
    description: p.tagline,
    brand: { '@type': 'Brand', name: 'IIY Phytoventra' },
    image: `https://iiypv.com${p.image}`,
    url: `https://iiypv.com/${p.slug}`,
    offers: {
      '@type': 'Offer',
      priceCurrency: 'GBP',
      availability: 'https://schema.org/PreOrder',
      url: `https://iiypv.com/${p.slug}`,
      seller: { '@type': 'Organization', name: 'IIY Phytoventra' },
    },
  };

  return (
    <div className="min-h-screen py-24 md:py-32 bg-white" data-testid="range-product-page">
      <SEO
        title={p.seoTitle || `${p.name} — ${p.category} | IIY Phytoventra`}
        description={p.seoDesc || `${p.name}: ${p.tagline} Coming soon from IIY Phytoventra.`}
        keywords={p.keywords}
        jsonLd={productLd}
      />
      <div className="max-w-6xl mx-auto px-6 md:px-12 lg:px-20">
        <button onClick={() => navigate('/products')} className="flex items-center gap-2 text-muted-foreground hover:text-primary mb-8 transition-colors" data-testid="range-back-button">
          <ArrowLeft className="w-5 h-5" /> Back to Products
        </button>

        <div className="grid lg:grid-cols-2 gap-12 items-start">
          <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} className="relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl ring-1 ring-emerald-900/10 aspect-[4/5]">
              <img src={p.image} alt={p.name} onClick={() => setLightboxOpen(true)} className="w-full h-full object-cover cursor-zoom-in" data-testid="range-main-image" />
              <div className="absolute inset-0 bg-gradient-to-t from-emerald-950/40 via-transparent to-transparent" />
              <div className="absolute top-5 left-5 bg-amber-500 text-emerald-950 text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1.5 rounded-full shadow-lg" data-testid="range-coming-soon-badge">Coming Soon</div>
            </div>
          </motion.div>

          <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} className="space-y-6">
            <div>
              <span className="text-sm font-medium tracking-widest uppercase text-emerald-700 mb-2 block">{p.category}</span>
              <h1 className="text-4xl md:text-5xl font-bold font-heading text-emerald-950 mb-3" data-testid="range-title">{p.name}</h1>
              <p className="text-lg text-emerald-800 font-medium mb-3">{p.tagline}</p>
              <p className="text-muted-foreground leading-relaxed">{p.intro}</p>
            </div>

            <div className="flex flex-wrap gap-2.5" data-testid="range-features">
              {p.features.map((f) => (
                <span key={f} className="inline-flex items-center gap-2 bg-emerald-50 border border-emerald-100 rounded-full px-4 py-2 text-sm font-medium text-emerald-900">
                  <CheckCircle className="w-4 h-4 text-emerald-600" />{f}
                </span>
              ))}
            </div>

            <div className="bg-stone-50 border-2 border-emerald-700/15 rounded-2xl p-6">
              <div className="flex items-center gap-2 mb-2 text-emerald-900 font-bold"><Sparkles className="w-4 h-4 text-amber-500" /> Launching soon</div>
              <p className="text-sm text-muted-foreground mb-4">This formulation is being finalised for the UK market. Join the list and we'll email you the moment it's available — including any first-time offer.</p>
              <button onClick={() => setNotify(true)} className="w-full inline-flex items-center justify-center gap-2 bg-emerald-800 hover:bg-emerald-900 text-white font-semibold py-3.5 rounded-xl transition-colors" data-testid="range-notify-btn">
                <BellRing className="w-4 h-4" /> Notify Me at Launch
              </button>
              <a href={`mailto:sales@iiypv.com?subject=${encodeURIComponent(p.name + ' Wholesale Enquiry')}`} className="mt-3 w-full inline-flex items-center justify-center gap-2 border-2 border-emerald-700 text-emerald-800 hover:bg-emerald-50 font-semibold py-3 rounded-xl transition-colors" data-testid="range-wholesale">
                <Mail className="w-4 h-4" /> Wholesale enquiry
              </a>
            </div>
          </motion.div>
        </div>

        {/* Key benefits */}
        <section className="mt-20 md:mt-28" data-testid="range-benefits-section">
          <div className="text-center mb-12">
            <span className="text-sm font-medium tracking-widest uppercase text-emerald-700 mb-3 block">Why {p.name}</span>
            <h2 className="text-3xl md:text-4xl font-bold font-heading text-emerald-950">Thoughtfully formulated, cleanly made.</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-6">
            {p.benefits.map((b, i) => (
              <div key={b.t} className="bg-white rounded-2xl p-6 border border-border/60 shadow-soft" data-testid={`range-benefit-${i}`}>
                <div className="w-11 h-11 rounded-xl bg-emerald-600/10 flex items-center justify-center mb-3"><Leaf className="w-5 h-5 text-emerald-700" /></div>
                <h3 className="font-semibold text-emerald-950 mb-1.5">{b.t}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{b.d}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Inside the blend */}
        {p.blend && (
          <section className="mt-20 md:mt-28" data-testid="range-blend-section">
            <div className="text-center mb-12">
              <span className="text-sm font-medium tracking-widest uppercase text-emerald-700 mb-3 block">Inside the blend</span>
              <h2 className="text-3xl md:text-4xl font-bold font-heading text-emerald-950">Thoughtfully chosen botanicals.</h2>
            </div>
            <div className={`grid gap-6 ${p.blend.length === 1 ? 'max-w-sm mx-auto' : p.blend.length === 3 ? 'sm:grid-cols-3' : 'sm:grid-cols-2 lg:grid-cols-4'}`}>
              {p.blend.map((ing, i) => (
                <motion.div
                  key={ing.name}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: i * 0.08 }}
                  className="bg-white rounded-2xl overflow-hidden border border-border/60 shadow-soft group"
                  data-testid={`range-blend-ingredient-${i}`}
                >
                  <div className="aspect-[4/3] overflow-hidden">
                    <img src={ing.img} alt={ing.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" data-testid={`range-blend-image-${i}`} />
                  </div>
                  <div className="p-5">
                    <h3 className="font-semibold text-emerald-950 mb-1.5">{ing.name}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{ing.note}</p>
                  </div>
                </motion.div>
              ))}
            </div>
            {p.blendNote && (
              <p className="text-xs text-muted-foreground mt-6 text-center max-w-2xl mx-auto leading-relaxed" data-testid="range-blend-note">{p.blendNote}</p>
            )}
          </section>
        )}

        {/* Ingredients + compliance */}
        <section className="mt-16 bg-stone-50 rounded-3xl p-8 md:p-10 border border-stone-200" data-testid="range-ingredients">
          <h3 className="text-lg font-bold text-emerald-950 mb-2">Formulation</h3>
          <p className="text-sm text-muted-foreground leading-relaxed">{p.ingredients}</p>

          {p.supplementFacts && (
            <div className="mt-7" data-testid="range-supplement-facts">
              <h4 className="text-base font-bold text-emerald-950 mb-1">Supplement Facts</h4>
              <p className="text-xs text-muted-foreground mb-3">Amount per serving</p>
              <div className="divide-y divide-stone-200 rounded-xl border border-stone-200 bg-white px-4">
                <div className="flex justify-between py-2.5 text-[11px] uppercase tracking-wide font-bold text-stone-500 gap-3">
                  <span className="flex-1">Nutrient</span><span className="w-24 text-right">Amount</span><span className="w-14 text-right">%NRV</span>
                </div>
                {p.supplementFacts.map((f) => (
                  <div key={f.name} className="flex justify-between items-center py-2.5 text-sm gap-3">
                    <span className="text-emerald-950 flex-1">{f.name}</span>
                    <span className="font-semibold text-stone-700 w-24 text-right">{f.amount}</span>
                    <span className="text-stone-500 w-14 text-right">{f.nrv}</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          <p className="text-xs text-muted-foreground mt-5 leading-relaxed">
            Food supplements should not be used as a substitute for a varied and balanced diet and a healthy lifestyle. Botanicals are included as part of an active-living routine; no medicinal claims are made.
          </p>
        </section>
      </div>

      <NotifyMeDialog open={notify} onOpenChange={setNotify} categoryTitle={p.name} productId={`product-${p.slug}`} productName={p.name} />
      <ImageLightbox open={lightboxOpen} images={[p.image]} index={0} altFor={() => p.name} onClose={() => setLightboxOpen(false)} />
    </div>
  );
}
